// src/config/n8n.ts

import {
  DATABASE_ROUTING_CONFIGURED,
  RUNTIME_DB,
  selectDatabaseEndpoint,
} from "./runtimeDb";

console.log("🧪 ENV sanity check", {
  NODE_ENV: process.env.NODE_ENV,
  USE_TEST: process.env.REACT_APP_N8N_USE_TEST,
  V3_URL: process.env.REACT_APP_N8N_WEBHOOK_V3_URL,
  V3TEST_URL: process.env.REACT_APP_N8N_WEBHOOK_V3TEST_URL,
  ABC_GAME_URL: process.env.REACT_APP_N8N_ABC_GAME_URL,
  TARGET_GAME_URL: process.env.REACT_APP_N8N_TARGET_GAME_URL,
  EXPECTANCY_TRACKER_URL: process.env.REACT_APP_N8N_EXPECTANCY_TRACKER_URL,
});

// Choose which webhook set to use in ONE place.
// Flip REACT_APP_N8N_USE_TEST=true to use the *test* URLs.
const useTest =
  (process.env.REACT_APP_N8N_USE_TEST || "").toLowerCase() === "true";

function selectEndpoint({
  airtableUrl,
  supabaseUrl,
  legacyUrl,
  testUrl,
}: {
  airtableUrl?: string;
  supabaseUrl?: string;
  legacyUrl?: string;
  testUrl?: string;
}): string | undefined {
  if (useTest) return testUrl?.trim() || undefined;

  return selectDatabaseEndpoint({
    db: RUNTIME_DB,
    airtableUrl,
    supabaseUrl,
    legacyUrl,
    databaseRoutingConfigured: DATABASE_ROUTING_CONFIGURED,
  });
}

// Main chat webhook
export const N8N_WEBHOOK_URL = selectEndpoint({
  airtableUrl: process.env.REACT_APP_N8N_WEBHOOK_V3_AIRTABLE_URL,
  supabaseUrl: process.env.REACT_APP_N8N_WEBHOOK_V3_SUPABASE_URL,
  legacyUrl: process.env.REACT_APP_N8N_WEBHOOK_V3_URL,
  testUrl: process.env.REACT_APP_N8N_WEBHOOK_V3TEST_URL,
});

// Timer-event webhook
export const N8N_TIMER_EVENT_URL = useTest
  ? (process.env.REACT_APP_N8N_TIMER_EVENT_TEST_URL as string)
  : (process.env.REACT_APP_N8N_TIMER_EVENT_URL as string);

// File-upload webhook (mirrors prod/test toggle)
export const N8N_FILE_UPLOAD_URL = useTest
  ? (process.env.REACT_APP_N8N_FILE_UPLOAD_TEST_URL as string)
  : (process.env.REACT_APP_N8N_FILE_UPLOAD_URL as string);

// Init webhook (separate from chat)
export const N8N_INIT_URL = selectEndpoint({
  airtableUrl: process.env.REACT_APP_N8N_INIT_AIRTABLE_URL,
  supabaseUrl: process.env.REACT_APP_N8N_INIT_SUPABASE_URL,
  legacyUrl: process.env.REACT_APP_N8N_INIT_URL,
  testUrl: process.env.REACT_APP_N8N_INIT_TEST_URL,
});

// Nudge-event webhook
export const N8N_NUDGE_EVENT_URL = useTest
  ? (process.env.REACT_APP_N8N_NUDGE_EVENT_TEST_URL as string)
  : (process.env.REACT_APP_N8N_NUDGE_EVENT_URL as string);

// Update coins/items webhook
export const N8N_UPDATE_COINS_ITEMS_URL = selectEndpoint({
  airtableUrl: process.env.REACT_APP_N8N_UPDATE_COINS_ITEMS_AIRTABLE_URL,
  supabaseUrl: process.env.REACT_APP_N8N_UPDATE_COINS_ITEMS_SUPABASE_URL,
  legacyUrl: process.env.REACT_APP_N8N_UPDATE_COINS_ITEMS_URL,
  testUrl: process.env.REACT_APP_N8N_UPDATE_COINS_ITEMS_TEST_URL,
});

// ABC Game webhook
// NOTE: this does NOT use the useTest toggle.
// Render dev/prod services use the same env var name with different values.
export const N8N_ABC_GAME_URL =
  process.env.REACT_APP_N8N_ABC_GAME_URL as string;

// Target Game webhook
// NOTE: this does NOT use the useTest toggle.
// Render dev/prod services use the same env var name with different values.
export const N8N_TARGET_GAME_URL =
  process.env.REACT_APP_N8N_TARGET_GAME_URL as string;

// Temporary production debug — remove after confirming Render env is loaded.
// eslint-disable-next-line no-console
console.log("[config] TARGET GAME FINAL URL", N8N_TARGET_GAME_URL);

// Expectancy Tracker webhook
// NOTE: this does NOT use the useTest toggle.
// Render dev/prod services use the same env var name with different values.
export const N8N_EXPECTANCY_TRACKER_URL =
  process.env.REACT_APP_N8N_EXPECTANCY_TRACKER_URL as string;

// ---- sanity checks (warn in dev/console only) ----
if (!N8N_WEBHOOK_URL) {
  // eslint-disable-next-line no-console
  console.error(
    `[config] Missing N8N ${RUNTIME_DB} chat webhook URL.`
  );
}

if (!N8N_TIMER_EVENT_URL) {
  // eslint-disable-next-line no-console
  console.error(
    "[config] Missing N8N timer-event webhook URL. Check your .env (TIMER_EVENT_URL / TIMER_EVENT_TEST_URL)."
  );
}

if (!N8N_INIT_URL) {
  // eslint-disable-next-line no-console
  console.error(`[config] Missing N8N ${RUNTIME_DB} init URL.`);
}

// Helpful warning if upload URL isn't set (the UI may fall back to /api/upload)
if (!N8N_FILE_UPLOAD_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    "[config] Missing N8N file-upload URL. The UI may fall back to /api/upload."
  );
}

if (!N8N_ABC_GAME_URL) {
  // eslint-disable-next-line no-console
  console.warn("[config] Missing N8N ABC Game URL.");
}

if (!N8N_TARGET_GAME_URL) {
  // eslint-disable-next-line no-console
  console.warn("[config] Missing N8N Target Game URL.");
}

if (!N8N_EXPECTANCY_TRACKER_URL) {
  // eslint-disable-next-line no-console
  console.warn("[config] Missing N8N Expectancy Tracker URL.");
}

// Dev-only debug log (avoid noisy logs in production)
if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.log("[config]", {
    useTest,
    RUNTIME_DB,
    DATABASE_ROUTING_CONFIGURED,
    N8N_WEBHOOK_URL,
    N8N_TIMER_EVENT_URL,
    N8N_INIT_URL,
    N8N_FILE_UPLOAD_URL,
    N8N_NUDGE_EVENT_URL,
    N8N_UPDATE_COINS_ITEMS_URL,
    N8N_ABC_GAME_URL,
    N8N_TARGET_GAME_URL,
    N8N_EXPECTANCY_TRACKER_URL,
  });
}
